import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useGetWellbeingSettings, getGetWellbeingSettingsQueryKey } from "@workspace/api-client-react";
import { DEFAULT_WORK_HOURS_BY_DAY, type WorkHoursByDay } from "@/pages/inbox/calendarUtils";

// A compact "where am I in the working day" strip for the header — left edge
// is today's configured start hour, right edge is the end hour, and the dot
// marks the current time (clamped to the bar before/after hours, rather than
// disappearing, so it's still meaningful outside the working window).
// Wellbeing-gated (WellbeingPane.tsx's "Working day timeline" toggle) since
// an ambient clock in the nav isn't something everyone wants — self-hides
// when off, so callers can just always mount it.
export function WorkingDayTimeline() {
  const { data: settings } = useGetWellbeingSettings({ query: { queryKey: getGetWellbeingSettingsQueryKey() } });
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  if (!settings?.workingDayTimelineEnabled) return null;

  const workHoursByDay = (settings.workHoursByDay as WorkHoursByDay | undefined) ?? DEFAULT_WORK_HOURS_BY_DAY;
  const [startHour, endHour] = workHoursByDay[now.getDay()] ?? DEFAULT_WORK_HOURS_BY_DAY[now.getDay()]!;
  // A zero-width range (start === end) is WorkHoursEditor's "Off" toggle —
  // today just isn't a working day, so there's no start/end to show a span
  // between rather than a real (if degenerate) 0%-progress bar.
  const isOff = startHour === endHour;
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const span = endHour - startHour;
  const pct = span > 0 ? Math.min(100, Math.max(0, ((nowHour - startHour) / span) * 100)) : 0;
  const fmt = (h: number) => format(new Date(2000, 0, 1, Math.floor(h), (h % 1) * 60), "h:mma");

  return (
    // Same box formula as the back/forward button cluster it sits next to —
    // no fixed height on the outer box, just p-0.5 around an h-7 inner row,
    // so the two stay pixel-identical instead of relying on a guessed height.
    <div
      className="relative z-10 flex shrink-0 items-center rounded-md border-2 border-border p-0.5"
      title={isOff ? "Not a working day" : `Working day ${fmt(startHour)}–${fmt(endHour)} — now ${format(now, "h:mm a")}`}
    >
      {isOff ? (
        <div className="flex h-7 items-center px-2">
          <span className="text-[11px] font-medium italic text-muted-foreground/60">Not a working day</span>
        </div>
      ) : (
        <div className="flex h-7 items-center gap-2 px-2">
          <span className="text-[11px] font-medium text-muted-foreground">{fmt(startHour)}</span>
          <div className="relative h-1.5 w-20 shrink-0 rounded-full bg-secondary">
            <div className="absolute inset-y-0 left-0 rounded-full bg-primary/30" style={{ width: `${pct}%` }} />
            <div
              className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card bg-primary"
              style={{ left: `${pct}%` }}
            />
          </div>
          <span className="text-[11px] font-medium text-muted-foreground">{fmt(endHour)}</span>
        </div>
      )}
    </div>
  );
}
